<template>
  <div class="GameList">
    <t-layout>
      <t-header class="page-header">
        <h1>游戏列表</h1>
        <t-button
          v-if="owner === '0'"
          theme="primary"
          @click="openAddDialog"
        >
          新增游戏
        </t-button>
      </t-header>

      <t-content class="page-content">
        <t-input
          v-model="searchQuery"
          type="search"
          clearable
          placeholder="搜索游戏ID或名称"
          class="search-input"
          @change="handleSearchChange"
        />

        <t-table
          v-if="games.length > 0"
          row-key="gameid"
          :data="games"
          :columns="tableColumns"
          :sort="tableSort"
          table-layout="auto"
          hover
          @sort-change="handleSortChange"
        >
          <template #title="{ row }">
            <span class="game-title-cell">
              <span>{{ row.title }}</span>
              <span v-if="Number(row.gameTypes) === 1" class="d-badge">D</span>
            </span>
          </template>

          <template #postTime="{ row }">
            <span class="post-time-cell">{{ row.postTime }}</span>
          </template>

          <template #types="{ row }">
            <t-tag
              :theme="row.types === '0' ? 'success' : 'default'"
              variant="light"
            >
              {{ row.types === "0" ? "正常" : "停售" }}
            </t-tag>
          </template>

          <template #djiami="{ row }">
            <t-tag
              :theme="
                row.djiami === '0'
                  ? 'default'
                  : row.djiami === '1'
                    ? 'warning'
                    : 'success'
              "
              variant="light"
            >
              {{
                row.djiami === "0"
                  ? "单机"
                  : row.djiami === "1"
                    ? "联机"
                    : "本体可联机"
              }}
            </t-tag>
          </template>

          <template #zs="{ row }">
            <t-tag
              :theme="row.zs === '1' ? 'success' : 'default'"
              variant="light"
            >
              {{ row.zs === "1" ? "赠送游戏" : "非赠送" }}
            </t-tag>
          </template>

          <template #operation="{ row }">
            <div class="operation-buttons">
              <t-button
                theme="primary"
                size="small"
                @click="openEditDialog(row)"
              >
                编辑
              </t-button>
              <t-button
                theme="danger"
                size="small"
                @click="openDeleteDialog(row)"
              >
                删除
              </t-button>
            </div>
          </template>
        </t-table>

        <t-empty v-else description="没有数据可显示" />

        <t-pagination
          v-if="total > 0"
          :current="currentPage"
          :page-size="pageSize"
          :page-size-options="[10, 20, 30, 40]"
          :total="total"
          show-page-size
          show-jumper
          show-first-and-last-page-btn
          total-content
          class="pagination"
          @current-change="handleCurrentChange"
          @page-size-change="handleSizeChange"
        />

        <!-- 编辑游戏 -->
        <t-dialog
          v-model:visible="dialogVisible"
          header="编辑游戏"
          :mode="isMobile ? 'full-screen' : 'modal'"
          :width="isMobile ? undefined : '50%'"
          :cancel-btn="null"
          :confirm-btn="null"
          @close="resetForm"
        >
          <t-form
            :data="currentGame"
            label-width="100px"
            class="game-form"
          >
            <t-form-item label="游戏ID" name="gameid">
              <t-input v-model="currentGame.gameid" disabled />
            </t-form-item>

            <t-form-item label="游戏名称" name="title">
              <t-input v-model="currentGame.title" />
            </t-form-item>

            <t-form-item label="封面图" name="photo">
              <t-input v-model="currentGame.photo" />
            </t-form-item>

            <t-form-item label="豪华版价格" name="price">
              <t-input v-model="currentGame.price" type="number" />
            </t-form-item>

            <t-form-item label="终极版价格" name="super_price">
              <t-input v-model="currentGame.super_price" type="number" />
            </t-form-item>

            <t-form-item label="上架时间" name="postTime">
              <t-date-picker
                v-model="currentGame.postTime"
                enable-time-picker
                allow-input
                format="YYYY-MM-DD HH:mm:ss"
                value-type="YYYY-MM-DD HH:mm:ss"
                placeholder="选择日期时间"
              />
            </t-form-item>

            <t-form-item label="游戏类型" name="gameTypes">
              <t-select
                v-model="currentGame.gameTypes"
                :options="gameTypeOptions"
              />
            </t-form-item>

            <t-form-item label="状态" name="types">
              <t-select
                v-model="currentGame.types"
                :options="statusOptions"
              />
            </t-form-item>

            <t-form-item label="离线账号" name="username">
              <t-input v-model="currentGame.username" />
            </t-form-item>

            <t-form-item label="离线密码" name="password">
              <t-input v-model="currentGame.password" />
            </t-form-item>

            <t-form-item label="分类" name="category">
              <t-select
                v-model="currentGame.category"
                :options="categoryOptions"
                placeholder="选择分类"
              />
            </t-form-item>

            <t-form-item label="联机状态" name="djiami">
              <t-select
                v-model="currentGame.djiami"
                :options="networkStatusOptions"
              />
            </t-form-item>

            <t-form-item label="赠送状态" name="zs">
              <t-select
                v-model="currentGame.zs"
                :options="giftStatusOptions"
              />
            </t-form-item>

            <t-form-item label="授权规则" name="auth_rule">
              <div class="auth-rule-panel">
                <div class="rule-mode-row">
                  <span>规则模式</span>
                  <span>默认规则</span>
                  <t-switch
                    v-model="authRuleForms.edit.enabled"
                    @change="updateAuthRuleFromForm('edit')"
                  />
                  <span class="custom-rule-text">自定义规则</span>
                </div>

                <t-alert
                  v-if="authRuleForms.edit.enabled"
                  message="开启后按当前游戏自定义规则发放 depot / ticket / token；关闭则走 djiami + codin 默认规则。"
                  theme="info"
                  :close="false"
                />

                <div
                  v-if="authRuleForms.edit.enabled"
                  class="rule-sections"
                >
                  <t-divider align="left">Depot 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`edit-depot-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">发放方式</div>
                          <t-select
                            v-model="authRuleForms.edit.depot[codin.key].mode"
                            :options="depotModeOptions"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>

                        <div
                          v-if="authRuleForms.edit.depot[codin.key].mode === 'types'"
                          class="rule-field"
                        >
                          <div class="rule-field-label">types</div>
                          <t-input
                            v-model="authRuleForms.edit.depot[codin.key].types"
                            placeholder="例如：1,2"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.edit.depot[codin.key].mode ===
                            'custom_depots'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">depots</div>
                          <t-input
                            v-model="authRuleForms.edit.depot[codin.key].depots"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <t-divider align="left">Ticket 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`edit-ticket-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">ticket</div>
                          <t-select
                            v-model="authRuleForms.edit.ticket[codin.key].mode"
                            :options="ticketModeOptions"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.edit.ticket[codin.key].mode ===
                            'custom_tickets'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">ticket_id</div>
                          <t-input
                            v-model="authRuleForms.edit.ticket[codin.key].ticket_ids"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <t-divider align="left">Token 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`edit-token-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">Token方式</div>
                          <t-select
                            v-model="authRuleForms.edit.token[codin.key].mode"
                            :options="tokenModeOptions"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.edit.token[codin.key].mode ===
                            'custom_appids'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">appids</div>
                          <t-input
                            v-model="authRuleForms.edit.token[codin.key].appids"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('edit')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </t-form-item>
          </t-form>

          <template #footer>
            <div class="dialog-footer">
              <t-button variant="outline" @click="dialogVisible = false">
                取消
              </t-button>
              <t-button theme="primary" @click="saveGame">保存</t-button>
            </div>
          </template>
        </t-dialog>

        <!-- 新增游戏 -->
        <t-dialog
          v-model:visible="addDialogVisible"
          header="新增游戏"
          :mode="isMobile ? 'full-screen' : 'modal'"
          :width="isMobile ? undefined : '50%'"
          :cancel-btn="null"
          :confirm-btn="null"
          @close="resetAddForm"
        >
          <t-form
            ref="addFormRef"
            :data="addForm"
            :rules="addRules"
            label-width="100px"
            class="game-form"
            @submit="submitAddGame"
          >
            <t-form-item label="游戏ID" name="gameid">
              <t-input
                v-model="addForm.gameid"
                @blur="onGameIdBlur"
              />
            </t-form-item>

            <t-form-item label="游戏名称" name="title">
              <t-input v-model="addForm.title" />
            </t-form-item>

            <t-form-item label="封面图" name="photo">
              <t-input
                v-model="addForm.photo"
                placeholder="https://..."
              />
            </t-form-item>

            <t-form-item label="豪华版价格" name="price">
              <t-input v-model="addForm.price" type="number" />
            </t-form-item>

            <t-form-item label="终极版价格" name="super_price">
              <t-input v-model="addForm.super_price" type="number" />
            </t-form-item>

            <t-form-item label="上架时间" name="postTime">
              <t-date-picker
                v-model="addForm.postTime"
                enable-time-picker
                allow-input
                format="YYYY-MM-DD HH:mm:ss"
                value-type="YYYY-MM-DD HH:mm:ss"
                placeholder="选择日期时间"
              />
            </t-form-item>

            <t-form-item label="游戏类型" name="gameTypes">
              <t-select
                v-model="addForm.gameTypes"
                :options="gameTypeOptions"
                placeholder="请选择"
              />
            </t-form-item>

            <t-form-item label="状态" name="types">
              <t-select
                v-model="addForm.types"
                :options="statusOptions"
                placeholder="请选择"
              />
            </t-form-item>

            <t-form-item label="离线账号" name="username">
              <t-input v-model="addForm.username" />
            </t-form-item>

            <t-form-item label="离线密码" name="password">
              <t-input v-model="addForm.password" />
            </t-form-item>

            <t-form-item label="分类" name="category">
              <t-select
                v-model="addForm.category"
                :options="categoryOptions"
                placeholder="选择分类"
              />
            </t-form-item>

            <t-form-item label="联机状态" name="djiami">
              <t-select
                v-model="addForm.djiami"
                :options="networkStatusOptions"
                placeholder="请选择"
              />
            </t-form-item>

            <t-form-item label="赠送状态" name="zs">
              <t-select
                v-model="addForm.zs"
                :options="giftStatusOptions"
                placeholder="请选择"
              />
            </t-form-item>

            <t-form-item label="授权规则" name="auth_rule">
              <div class="auth-rule-panel">
                <div class="rule-mode-row">
                  <span>规则模式</span>
                  <span>默认规则</span>
                  <t-switch
                    v-model="authRuleForms.add.enabled"
                    @change="updateAuthRuleFromForm('add')"
                  />
                  <span class="custom-rule-text">自定义规则</span>
                </div>

                <t-alert
                  v-if="authRuleForms.add.enabled"
                  message="开启后按当前游戏自定义规则发放 depot / ticket / token；关闭则走 djiami + codin 默认规则。"
                  theme="info"
                  :close="false"
                />

                <div
                  v-if="authRuleForms.add.enabled"
                  class="rule-sections"
                >
                  <t-divider align="left">Depot 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`add-depot-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">发放方式</div>
                          <t-select
                            v-model="authRuleForms.add.depot[codin.key].mode"
                            :options="depotModeOptions"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>

                        <div
                          v-if="authRuleForms.add.depot[codin.key].mode === 'types'"
                          class="rule-field"
                        >
                          <div class="rule-field-label">types</div>
                          <t-input
                            v-model="authRuleForms.add.depot[codin.key].types"
                            placeholder="例如：1,2"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.add.depot[codin.key].mode ===
                            'custom_depots'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">depots</div>
                          <t-input
                            v-model="authRuleForms.add.depot[codin.key].depots"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <t-divider align="left">Ticket 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`add-ticket-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">ticket</div>
                          <t-select
                            v-model="authRuleForms.add.ticket[codin.key].mode"
                            :options="ticketModeOptions"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.add.ticket[codin.key].mode ===
                            'custom_tickets'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">ticket_id</div>
                          <t-input
                            v-model="authRuleForms.add.ticket[codin.key].ticket_ids"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <t-divider align="left">Token 规则</t-divider>
                  <div class="rule-grid">
                    <div
                      v-for="codin in codinKeys"
                      :key="`add-token-${codin.key}`"
                      class="rule-card"
                    >
                      <div class="rule-card-title">{{ codin.label }}</div>
                      <div class="rule-card-body">
                        <div class="rule-field">
                          <div class="rule-field-label">Token方式</div>
                          <t-select
                            v-model="authRuleForms.add.token[codin.key].mode"
                            :options="tokenModeOptions"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>

                        <div
                          v-if="
                            authRuleForms.add.token[codin.key].mode ===
                            'custom_appids'
                          "
                          class="rule-field"
                        >
                          <div class="rule-field-label">appids</div>
                          <t-input
                            v-model="authRuleForms.add.token[codin.key].appids"
                            placeholder="例如：123456,123457"
                            @change="updateAuthRuleFromForm('add')"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </t-form-item>
          </t-form>

          <template #footer>
            <div class="dialog-footer">
              <t-button variant="outline" @click="addDialogVisible = false">
                取消
              </t-button>
              <t-button theme="primary" @click="submitAddGame">
                保存
              </t-button>
            </div>
          </template>
        </t-dialog>

        <!-- 删除确认 -->
        <t-dialog
          v-model:visible="deleteDialogVisible"
          header="你确定要删除该游戏吗？"
          width="400px"
          :cancel-btn="null"
          :confirm-btn="null"
          :close-on-overlay-click="false"
          @close="onCancelDelete"
        >
          <div class="delete-content">
            <div>游戏ID：{{ gameToDelete.gameid }}</div>
            <div>游戏名称：{{ gameToDelete.title }}</div>
            <div>上架时间：{{ gameToDelete.postTime }}</div>
            <p class="delete-warning">
              删除游戏后无法恢复，对应manifaset和depot都会直接删除。
            </p>
          </div>

          <template #footer>
            <div class="dialog-footer">
              <t-button variant="outline" @click="onCancelDelete">
                取消
              </t-button>
              <t-button
                theme="danger"
                :disabled="deleteCountdown > 0"
                @click="onConfirmDelete"
              >
                确定 ({{ deleteCountdown }})
              </t-button>
            </div>
          </template>
        </t-dialog>
      </t-content>
    </t-layout>
  </div>
</template>

<script>
import axios from "../axios";
import { computed } from "vue";
import { useStore } from "vuex";
import {
  Alert as TAlert,
  Button as TButton,
  Content as TContent,
  DatePicker as TDatePicker,
  Dialog as TDialog,
  Divider as TDivider,
  Empty as TEmpty,
  Form as TForm,
  FormItem as TFormItem,
  Header as THeader,
  Input as TInput,
  Layout as TLayout,
  MessagePlugin,
  Pagination as TPagination,
  Select as TSelect,
  Switch as TSwitch,
  Table as TTable,
  Tag as TTag,
} from "tdesign-vue-next";

const createRuleGroup = (depotMode, ticketMode, tokenMode) => ({
  depot: {
    mode: depotMode,
    types: depotMode === "types" ? "1" : "",
    depots: "",
  },
  ticket: { mode: ticketMode, ticket_ids: "" },
  token: { mode: tokenMode, appids: "" },
});

const createDefaultAuthRuleForm = () => ({
  enabled: false,
  depot: {
    codin_1: { ...createRuleGroup("types", "self", "self_token").depot },
    codin_2: { ...createRuleGroup("all", "all", "all_depot").depot },
  },
  ticket: {
    codin_1: { ...createRuleGroup("types", "self", "self_token").ticket },
    codin_2: { ...createRuleGroup("all", "all", "all_depot").ticket },
  },
  token: {
    codin_1: { ...createRuleGroup("types", "self", "self_token").token },
    codin_2: { ...createRuleGroup("all", "all", "all_depot").token },
  },
});

const createDefaultAddForm = () => ({
  gameid: "",
  title: "",
  photo: "",
  price: "",
  super_price: "",
  postTime: "",
  types: "1",
  gameTypes: "0",
  username: "",
  password: "",
  category: "",
  djiami: "0",
  zs: "0",
  auth_rule: "",
});

const createDefaultCurrentGame = () => ({
  gameid: "",
  title: "",
  photo: "",
  price: "",
  super_price: "",
  postTime: "",
  types: "0",
  gameTypes: "0",
  username: "",
  password: "",
  category: "",
  djiami: "",
  zs: "",
  auth_rule: "",
});

const formatDateTime = (date = new Date()) => {
  const pad = (value) => String(value).padStart(2, "0");
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join(" ");
};

export default {
  name: "GameList",
  components: {
    TAlert,
    TButton,
    TContent,
    TDatePicker,
    TDialog,
    TDivider,
    TEmpty,
    TForm,
    TFormItem,
    THeader,
    TInput,
    TLayout,
    TPagination,
    TSelect,
    TSwitch,
    TTable,
    TTag,
  },
  setup() {
    const store = useStore();
    const owner = computed(() => store.state.user.owner);
    const isMobile = computed(() => window.innerWidth <= 768);
    return { owner, isMobile };
  },
  data() {
    return {
      games: [],
      dialogVisible: false,
      addDialogVisible: false,
      deleteDialogVisible: false,

      authRuleForms: {
        edit: createDefaultAuthRuleForm(),
        add: createDefaultAuthRuleForm(),
      },

      codinKeys: [
        { key: "codin_1", label: "豪华版 codin_1" },
        { key: "codin_2", label: "终极版 codin_2" },
      ],

      depotModeOptions: [
        { value: "types", label: "按 types 发放" },
        { value: "all", label: "全部 depot" },
        { value: "before_usetime", label: "使用时间前 depot" },
        { value: "exclude_body", label: "排除本体 depot" },
        { value: "custom_depots", label: "指定 depot" },
      ],

      ticketModeOptions: [
        { value: "self", label: "仅本体 ticket" },
        { value: "all", label: "全部 ticket" },
        { value: "custom_tickets", label: "指定 ticket" },
        { value: "none", label: "不发 ticket" },
      ],

      tokenModeOptions: [
        { value: "self_token", label: "只发本体 token" },
        { value: "all_depot", label: "全发 depot token" },
        { value: "depot", label: "按当前 depot 查询 token" },
        { value: "custom_appids", label: "指定 appid 查询 token" },
        { value: "none", label: "不发 token" },
      ],

      gameTypeOptions: [
        { label: "正常游戏", value: "0" },
        { label: "D加密游戏", value: "1" },
      ],

      statusOptions: [
        { label: "正常", value: "0" },
        { label: "停售", value: "1" },
      ],

      networkStatusOptions: [
        { label: "单机", value: "0" },
        { label: "联机", value: "1" },
        { label: "本体可联机", value: "2" },
      ],

      giftStatusOptions: [
        { label: "非赠送", value: "0" },
        { label: "赠送", value: "1" },
      ],

      addForm: createDefaultAddForm(),

      addRules: {
        gameid: [
          { required: true, message: "请输入游戏ID", trigger: "blur" },
        ],
        title: [
          { required: true, message: "请输入游戏名称", trigger: "blur" },
        ],
        photo: [
          { required: true, message: "请输入封面图URL", trigger: "blur" },
        ],
        price: [
          { required: true, message: "请输入价格", trigger: "blur" },
        ],
        super_price: [
          { required: true, message: "请输入价格", trigger: "blur" },
        ],
        postTime: [
          { required: true, message: "请选择上架时间", trigger: "change" },
        ],
        types: [
          { required: true, message: "请选择状态", trigger: "change" },
        ],
        gameTypes: [
          { required: true, message: "请选择游戏类型", trigger: "change" },
        ],
        username: [
          { required: true, message: "请输入离线账号", trigger: "blur" },
        ],
        password: [
          { required: true, message: "请输入离线密码", trigger: "blur" },
        ],
        category: [
          { required: true, message: "请选择分类", trigger: "change" },
        ],
        djiami: [
          { required: true, message: "请选择联机状态", trigger: "change" },
        ],
        zs: [
          { required: true, message: "请选择赠送状态", trigger: "change" },
        ],
        auth_rule: [
          {
            validator: (value) => {
              if (!value || !String(value).trim()) return true;
              try {
                JSON.parse(value);
                return true;
              } catch (error) {
                return {
                  result: false,
                  message: "自定义规则必须是合法 JSON",
                  type: "error",
                };
              }
            },
            trigger: "change",
          },
        ],
      },

      categories: [
        "休闲益智",
        "动作格斗",
        "角色扮演",
        "冒险解谜",
        "经营模拟",
        "射击游戏",
        "恐怖游戏",
      ],

      currentGame: createDefaultCurrentGame(),

      gameToDelete: {},
      deleteCountdown: 10,
      deleteInterval: null,

      searchQuery: "",
      currentPage: 1,
      pageSize: 10,
      total: 0,
      sort: { prop: "", order: "" },
    };
  },
  computed: {
    categoryOptions() {
      return this.categories.map((category) => ({
        label: category,
        value: category,
      }));
    },

    tableSort() {
      if (!this.sort.prop) return undefined;
      return {
        sortBy: this.sort.prop,
        descending: this.sort.order === "descending",
      };
    },

    tableColumns() {
      const columns = [
        {
          colKey: "gameid",
          title: "游戏ID",
          width: 150,
        },
        {
          colKey: "title",
          title: "游戏名称",
          width: 180,
          cell: "title",
        },
        {
          colKey: "price",
          title: "豪华版",
          width: 100,
        },
        {
          colKey: "super_price",
          title: "终极版",
          width: 100,
        },
      ];

      if (this.owner === "0") {
        columns.push({
          colKey: "postTime",
          title: "上架时间",
          width: 190,
          sorter: true,
          cell: "postTime",
        });
      }

      columns.push(
        {
          colKey: "types",
          title: "状态",
          width: 100,
          cell: "types",
        },
        {
          colKey: "category",
          title: "分类",
          width: 150,
        },
        {
          colKey: "djiami",
          title: "联机状态",
          width: 150,
          cell: "djiami",
        },
        {
          colKey: "zs",
          title: "赠送状态",
          width: 150,
          cell: "zs",
        }
      );

      if (this.owner === "0") {
        columns.push({
          colKey: "operation",
          title: "操作",
          width: 180,
          fixed: "right",
          cell: "operation",
        });
      }

      return columns;
    },
  },
  created() {
    this.fetchGames();
  },
  beforeUnmount() {
    if (this.deleteInterval) {
      clearInterval(this.deleteInterval);
      this.deleteInterval = null;
    }
  },
  methods: {
    async fetchGames() {
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.post("/index.php?action=getGames", {
          token,
          search: this.searchQuery,
          page: this.currentPage,
          size: this.pageSize,
          sort: this.sort,
        });
        this.games = response.data.games || [];
        this.total = Number.parseInt(response.data.total, 10) || 0;
      } catch (error) {
        console.error("Error fetching games:", error);
        MessagePlugin.error("获取游戏列表失败");
      }
    },

    handleSearchChange(value) {
      this.searchQuery = value ?? "";
      this.currentPage = 1;
      this.fetchGames();
    },

    handleSortChange(sort) {
      const currentSort = Array.isArray(sort) ? sort[0] : sort;

      if (!currentSort || !currentSort.sortBy) {
        this.sort = { prop: "", order: "" };
      } else {
        this.sort = {
          prop: currentSort.sortBy,
          order: currentSort.descending ? "descending" : "ascending",
        };
      }

      this.currentPage = 1;
      this.fetchGames();
    },

    openEditDialog(game) {
      this.currentGame = {
        ...game,
        gameTypes: String(game.gameTypes ?? 0),
        types: String(game.types ?? 0),
        djiami: String(game.djiami ?? 0),
        zs: String(game.zs ?? 0),
        auth_rule: game.auth_rule || "",
      };
      this.loadAuthRuleForm("edit", this.currentGame.auth_rule);
      this.dialogVisible = true;
    },

    async saveGame() {
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.post("/index.php?action=updateGame", {
          token,
          game: JSON.stringify({
            ...this.currentGame,
            price: Number(this.currentGame.price),
            super_price: Number(this.currentGame.super_price),
            gameTypes: Number(this.currentGame.gameTypes || 0),
            auth_rule: this.prepareAuthRulePayload(
              this.currentGame.auth_rule
            ),
          }),
        });

        if (response.data.success) {
          MessagePlugin.success("游戏信息已更新");
          this.dialogVisible = false;
          this.fetchGames();
        } else {
          MessagePlugin.error(response.data.message || "更新失败");
        }
      } catch (error) {
        console.error("Error updating game:", error);
        MessagePlugin.error("更新失败，请稍后重试");
      }
    },

    openAddDialog() {
      if (!this.addForm.postTime) {
        this.addForm.postTime = formatDateTime();
      }
      this.loadAuthRuleForm("add", this.addForm.auth_rule);
      this.addDialogVisible = true;
    },

    resetAddForm() {
      this.addForm = createDefaultAddForm();
      this.authRuleForms.add = createDefaultAuthRuleForm();

      this.$nextTick(() => {
        this.$refs.addFormRef?.clearValidate?.();
      });
    },

    async submitUpdate(gameid) {
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.post(
          "/index.php?action=addUpdateGameRecords",
          {
            token,
            gameid,
          }
        );

        if (response.data.success) {
          MessagePlugin.success("更新记录已添加");
        } else {
          MessagePlugin.error(response.data.message || "添加失败");
        }
      } catch (error) {
        console.error("提交更新记录失败:", error);
        MessagePlugin.error("提交失败");
      }
    },

    async fetchChineseName(gameid) {
      try {
        const response = await axios.get(
          process.env.VUE_APP_STEAM_PROXY_URL ||
            "http://localhost:3100/steam/proxy",
          {
            params: { appids: gameid },
          }
        );

        const data = response.data?.[gameid]?.data;
        if (data && data.name) {
          return data.name;
        }

        console.warn("未获取到中文名称");
        return null;
      } catch (error) {
        console.error("请求失败:", error);
        return null;
      }
    },

    async onGameIdBlur() {
      const gameid = String(this.addForm.gameid || "").trim();
      if (!gameid) return;

      try {
        const response = await axios.get(
          `https://api.steamcmd.net/v1/info/${gameid}`
        );
        const imgPath =
          response.data?.data?.[gameid]?.common?.library_assets_full
            ?.library_capsule?.image2x?.english;

        if (!imgPath) {
          MessagePlugin.warning("未找到封面图信息");
          return;
        }

        this.addForm.photo = `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${gameid}/${imgPath}`;

        const chineseName = await this.fetchChineseName(gameid);
        if (chineseName) {
          this.addForm.title = chineseName;
        }
      } catch (error) {
        console.error("获取封面或名称失败:", error);
        MessagePlugin.error("获取游戏信息失败");
      }
    },

    async submitAddGame() {
      const formRef = this.$refs.addFormRef;

      if (!formRef || typeof formRef.validate !== "function") {
        console.error("addFormRef 不是有效的 TDesign Form 实例:", formRef);
        MessagePlugin.error("表单组件初始化失败");
        return;
      }

      try {
        const validateResult = await formRef.validate({
          trigger: "submit",
          showErrorMessage: true,
        });

        if (validateResult !== true) return;

        const token = localStorage.getItem("user-token");
        const payload = {
          ...this.addForm,
          price: Number(this.addForm.price),
          super_price: Number(this.addForm.super_price),
          gameTypes: Number(this.addForm.gameTypes || 0),
          auth_rule: this.prepareAuthRulePayload(this.addForm.auth_rule),
        };

        const response = await axios.post("/index.php?action=addNewGame", {
          token,
          newGame: JSON.stringify(payload),
        });

        if (
          response.data &&
          (response.data.code === 200 || response.data.success)
        ) {
          const gameid = this.addForm.gameid;

          MessagePlugin.success("新增成功");
          this.addDialogVisible = false;
          this.resetAddForm();
          this.fetchGames();
          this.submitUpdate(gameid);
        } else {
          MessagePlugin.error(response.data?.message || "新增失败");
        }
      } catch (error) {
        console.error("新增游戏失败:", error);
        MessagePlugin.error("请求失败，请稍后重试");
      }
    },

    openDeleteDialog(game) {
      this.gameToDelete = game;
      this.deleteDialogVisible = true;
      this.deleteCountdown = 10;

      if (this.deleteInterval) {
        clearInterval(this.deleteInterval);
      }

      this.deleteInterval = setInterval(() => {
        if (this.deleteCountdown > 0) {
          this.deleteCountdown -= 1;
        } else {
          clearInterval(this.deleteInterval);
          this.deleteInterval = null;
        }
      }, 1000);
    },

    async onConfirmDelete() {
      if (this.deleteInterval) {
        clearInterval(this.deleteInterval);
        this.deleteInterval = null;
      }

      const gameid = this.gameToDelete.gameid || "";

      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.post("/index.php?action=deleteGame", {
          token,
          gameid,
        });

        if (response.data.success) {
          MessagePlugin.success("游戏已删除");
          this.fetchGames();
        } else {
          MessagePlugin.error(response.data.message || "删除失败");
        }
      } catch (error) {
        console.error("Error deleting game:", error);
        MessagePlugin.error("删除失败，请稍后重试");
      }

      this.deleteDialogVisible = false;
    },

    onCancelDelete() {
      if (this.deleteInterval) {
        clearInterval(this.deleteInterval);
        this.deleteInterval = null;
      }
      this.deleteDialogVisible = false;
    },

    handleSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.currentPage = 1;
      this.fetchGames();
    },

    handleCurrentChange(current) {
      this.currentPage = current;
      this.fetchGames();
    },

    resetForm() {
      this.currentGame = createDefaultCurrentGame();
      this.authRuleForms.edit = createDefaultAuthRuleForm();
    },

    getAuthRuleTarget(type) {
      return type === "add" ? this.addForm : this.currentGame;
    },

    loadAuthRuleForm(type, raw) {
      const form = createDefaultAuthRuleForm();

      if (!raw || !String(raw).trim()) {
        this.authRuleForms[type] = form;
        this.getAuthRuleTarget(type).auth_rule = "";
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        form.enabled = true;
        this.applyParsedRuleGroup(form, "depot", parsed.depot);
        this.applyParsedRuleGroup(form, "ticket", parsed.ticket);
        this.applyParsedRuleGroup(form, "token", parsed.token);
        this.authRuleForms[type] = form;
        this.updateAuthRuleFromForm(type);
      } catch (error) {
        console.error("授权规则解析失败:", error);
        this.authRuleForms[type] = form;
        this.getAuthRuleTarget(type).auth_rule = "";
        MessagePlugin.error("授权规则 JSON 格式不正确，已切回默认规则");
      }
    },

    applyParsedRuleGroup(form, group, parsedGroup) {
      if (!parsedGroup || typeof parsedGroup !== "object") return;

      this.codinKeys.forEach(({ key }) => {
        const parsed =
          parsedGroup[key] || (group === "token" ? parsedGroup : null);

        if (!parsed || typeof parsed !== "object") return;

        form[group][key].mode = parsed.mode || form[group][key].mode;

        if (Array.isArray(parsed.types)) {
          form[group][key].types = parsed.types.join(",");
        }
        if (Array.isArray(parsed.depots)) {
          form[group][key].depots = parsed.depots.join(",");
        }
        if (Array.isArray(parsed.ticket_ids)) {
          form[group][key].ticket_ids = parsed.ticket_ids.join(",");
        }
        if (Array.isArray(parsed.appids)) {
          form[group][key].appids = parsed.appids.join(",");
        }
      });
    },

    updateAuthRuleFromForm(type) {
      const target = this.getAuthRuleTarget(type);
      const form = this.authRuleForms[type];

      if (!form.enabled) {
        target.auth_rule = "";
      } else {
        target.auth_rule = JSON.stringify(
          this.buildAuthRulePayload(form),
          null,
          2
        );
      }
    },

    buildAuthRulePayload(form) {
      const payload = { depot: {}, ticket: {}, token: {} };

      this.codinKeys.forEach(({ key }) => {
        payload.depot[key] = this.buildDepotRule(form.depot[key]);
        payload.ticket[key] = this.buildTicketRule(form.ticket[key]);
        payload.token[key] = this.buildTokenRule(form.token[key]);
      });

      return payload;
    },

    buildDepotRule(rule) {
      const payload = { mode: rule.mode };

      if (rule.mode === "types") {
        payload.types = this.parseNumberList(rule.types || "1");
      }
      if (rule.mode === "custom_depots") {
        payload.depots = this.parseNumberList(rule.depots);
      }

      return payload;
    },

    buildTicketRule(rule) {
      const payload = { mode: rule.mode };

      if (rule.mode === "custom_tickets") {
        payload.ticket_ids = this.parseNumberList(rule.ticket_ids);
      }

      return payload;
    },

    buildTokenRule(rule) {
      const payload = { mode: rule.mode };

      if (rule.mode === "custom_appids") {
        payload.appids = this.parseNumberList(rule.appids);
      }

      return payload;
    },

    parseNumberList(value) {
      return String(value || "")
        .split(/[,\s，]+/)
        .map((item) => Number.parseInt(item, 10))
        .filter((item) => Number.isFinite(item));
    },

    prepareAuthRulePayload(value) {
      if (!value || !String(value).trim()) return null;
      return JSON.stringify(JSON.parse(value));
    },
  },
};
</script>

<style scoped>
.GameList {
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: auto;
  padding: 0 0 20px;
  background: #f7f9fc;
}

.page-header h1 {
  margin: 0;
}

.page-content {
  padding: 0;
  background: #f7f9fc;
}

.search-input {
  margin-bottom: 20px;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.game-title-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.post-time-cell {
  display: inline-block;
  min-width: 154px;
  white-space: nowrap;
  word-break: keep-all;
}

.d-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: #fff;
  background: var(--td-brand-color);
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.operation-buttons,
.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.game-form :deep(.t-input),
.game-form :deep(.t-select),
.game-form :deep(.t-date-picker) {
  width: 100%;
}

.auth-rule-panel {
  width: 100%;
  padding: 4px 0;
}

.rule-mode-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  color: var(--td-text-color-secondary);
}

.custom-rule-text {
  color: var(--td-brand-color);
}

.rule-sections {
  margin-top: 14px;
}

.rule-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 12px;
}

.rule-card {
  overflow: hidden;
  border: 1px solid var(--td-component-border);
  border-radius: var(--td-radius-medium);
  background: var(--td-bg-color-container);
}

.rule-card-title {
  padding: 12px 16px;
  border-bottom: 1px solid var(--td-component-border);
  color: var(--td-text-color-secondary);
  font-weight: 600;
}

.rule-card-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
}

.rule-field {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}

.rule-field-label {
  color: var(--td-text-color-secondary);
  text-align: right;
}

.delete-content {
  line-height: 1.9;
}

.delete-warning {
  margin: 10px 0 0;
  color: var(--td-error-color);
}

@media (max-width: 768px) {
  .GameList {
    padding: 12px;
  }

  .page-header {
    gap: 12px;
  }

  .page-header h1 {
    font-size: 22px;
  }

  .rule-grid {
    grid-template-columns: 1fr;
  }

  .rule-field {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .rule-field-label {
    text-align: left;
  }

  .dialog-footer {
    width: 100%;
  }

  .dialog-footer :deep(.t-button) {
    flex: 1;
  }

  .operation-buttons {
    flex-wrap: wrap;
  }
}
</style>
